<template>
    <dashboard
        :statusFilterOptions="statusFilterOptions"
        :riskTypeOptions="riskTypeOptions"
        :tableHeaders="tableHeaders"
        :endpoint="endpoint"
        :submission-route="submissionRoute"
        :manual-submission-route="manualSubmissionRoute"
        :statFilters="statFilters"
        :info="info"
        role="agent"
    >
        <template v-slot:tableRow="props">

            <td>
                {{ props.row.item.updated_at | formatDateEnglishShort}}
            </td>

            <td :inner-html.prop="props.row.item.id | highlightText(props.searchTerm)"/>

            <td>
                <div class="width-20">
                    <x-tooltip top>
                        <template v-slot:activator="{ on }">
                            <span v-on="on" @mouseover="scrollTextForwards" @mouseout="scrollTextBackwards" class='text-scroller ellipsis'>
                                {{ getRiskStructureShort(props.row.item.risk_structure) }}
                            </span>
                        </template>
                        <div>
                            {{ getRiskStructurePretty(props.row.item.risk_structure) }}
                        </div>
                    </x-tooltip>
                </div>
            </td>

            <td style="max-width: 100px">
                <span class='text-scroller client-scroller ellipsis' @mouseover="scrollTextForwards" @mouseout="scrollTextBackwards"
                    :inner-html.prop="props.row.item.client_company_name | highlightText(props.searchTerm)"/>
            </td>

            <td>
                <span v-if="!isMultiObligor(props.row.item)" @mouseover="scrollTextForwards" @mouseout="scrollTextBackwards" class='text-scroller ellipsis width-150'
                    :inner-html.prop="props.row.item.obligor_name_notification | highlightText(props.searchTerm)"
                />

                    <x-tooltip v-else top>
                        <template v-slot:activator="{ on }">
                            <span v-on="on" @mouseover="scrollTextForwards" @mouseout="scrollTextBackwards" class='text-scroller ellipsis width-150'
                                :inner-html.prop="props.row.item.obligor_name_notification | highlightText(props.searchTerm)"
                            />
                        </template>
                        <div v-if="props.row.item.obligors.length > 0">
                            <p>Obligors:</p>
                            <p v-for="obligor in props.row.item.obligors">&nbsp;-&nbsp;{{ obligor.name }}</p>
                        </div>
                        <div v-if="props.row.item.guarantors.length > 0">
                            <p>Guarantors:</p>
                            <p v-for="guarantor in props.row.item.guarantors">&nbsp;-&nbsp;{{ guarantor.name }}</p>
                        </div>
                    </x-tooltip>

            </td>

            <td style="max-width: 100px">
                <span v-if="!isMultiObligor(props.row.item)" @mouseover="scrollTextForwards" @mouseout="scrollTextBackwards" class='text-scroller ellipsis'>
                    {{ countryName(props.row.item) }}
                </span>
                <x-tooltip v-else top>
                    <template v-slot:activator="{ on }">
                        <span v-on="on" @mouseover="scrollTextForwards" @mouseout="scrollTextBackwards" class='text-scroller ellipsis'>
                            {{ countryName(props.row.item) }}
                        </span>
                    </template>
                    <div v-if="props.row.item.obligors.length > 0">
                        <p>Obligors country:</p>
                        <p v-for="obligorCountry in getUniqueCountryList(props.row.item.obligors)">&nbsp;-&nbsp;{{ obligorCountry }}</p>
                    </div>
                    <div v-if="props.row.item.guarantors.length > 0">
                        <p>Guarantors country:</p>
                        <p v-for="guarantorCountry in getUniqueCountryList(props.row.item.guarantors)">&nbsp;-&nbsp;{{ guarantorCountry }}</p>
                    </div>
                </x-tooltip>
            </td>

            <td style="max-width: 60px" :inner-html.prop="props.row.item.currency_code | highlightText(props.searchTerm)"/>

            <td class="text-xs-right" style="max-width: 150px">
                <span class='text-scroller ellipsis' @mouseover="scrollTextForwards" @mouseout="scrollTextBackwards">
                    {{ props.row.item.limit_of_liability | currency(props.row.item.currency_code) }}
                </span>
            </td>

            <td>
                {{ props.row.item.expiry_date | formatDateEnglishShort }}
            </td>

            <td class="child-flex text-xs-right">
                <action-icon
                    v-if="props.actions.offer_offered"
                    color="green"
                    icon="fa-dollar-sign"
                >
                    <span class="circle black">{{ props.actions.offer_offered.action.num_offers }}</span> offers received

                    <progress-linear-bar
                        v-model="props.actions.offer_offered.action.percent"
                        :appended-label="`${getOfferedPercentage(props.actions.offer_offered.action.percent)}% offered`"
                        height="6"
                        color="white"
                        background-opacity="0.5"
                    />
                </action-icon>

                <action-icon
                    v-if="props.actions.offer_with_queries"
                    color="yellow"
                    icon="fa-question"
                >
                    <span class="circle black">{{ props.actions.offer_with_queries.action.num_offers }}</span>
                    {{ Number(props.actions.offer_with_queries.action.num_offers) > 1 ? 'queries received' : 'query received' }}
                </action-icon>

                <action-icon
                    v-if="props.actions.pending_amendments"
                    color="blue"
                    icon="edit"
                >
                    <p>
                        {{ props.actions.pending_amendments.action.approved_amendments }} out of {{ props.actions.pending_amendments.action.total_amendments }}
                        <br>
                        <span class="light">underwriters accepted</span>
                    </p>

                    <p>
                        <span class="light">Effective date</span>
                        <br>
                        {{ $moment(props.actions.pending_amendments.action.effective_date).format('D MMM YYYY') }}
                    </p>
                </action-icon>

                <action-icon
                    v-if="props.actions.pending_off_risk"
                    color="grey"
                    icon="fa-power-off"
                >
                    <p>
                        {{ props.actions.pending_off_risk.action.approved_amendments }} out of {{ props.actions.pending_off_risk.action.total_amendments }}
                        <br>
                        <span class="light">underwriters accepted</span>
                    </p>
                    <p>
                        <span class="light">Closing date</span>
                        <br>
                        {{ $moment(props.actions.pending_off_risk.action.effective_date).format('D MMM YYYY') }}
                    </p>
                </action-icon>

                <action-icon
                    v-if="props.actions.pending_mid_term_adjustment"
                    color="grey"
                    icon="fab fa-product-hunt"
                >
                    <p>
                        {{ props.actions.pending_mid_term_adjustment.action.approved_amendments }} out of {{ props.actions.pending_mid_term_adjustment.action.total_amendments }}
                        <br>
                        <span class="light">underwriters accepted</span>
                    </p>

                    <p>
                        <span class="light">Effective date</span>
                        <br>
                        {{ $moment(props.actions.pending_mid_term_adjustment.action.effective_date).format('D MMM YYYY') }}
                    </p>
                </action-icon>

                <action-icon
                    v-if="props.row.item.firm_order_last_requested_at"
                    color="cobalt"
                    icon="fa-gavel"
                >
                    <p>
                        Firm order requested
                        <br>
                        <span class="light">
                            {{ $moment(props.row.item.firm_order_last_requested_at).format('DD MMM YYYY kk:mm') }}
                        </span>
                    </p>
                </action-icon>

                <action-icon
                    v-if="props.row.item.creation_method === 'automated'"
                    color="teal"
                    icon="fa-bolt"
                >
                    <p>
                        Automated submission
                        <span class="light" v-if="props.row.item.client_reference" style="font-family: monospace">
                            <br>
                            {{ props.row.item.client_reference }}
                        </span>
                    </p>
                </action-icon>

                <status-chip small :status="props.row.item.status" class="ma-0" />
            </td>
        </template>
    </dashboard>
</template>

<script>
    import Dashboard from '~/components/Shared/Partials/Dashboard';
    import StatusChip from '~/components/Shared/UIComponents/StatusChip';
    import ActionIcon from '~/components/Shared/UIComponents/ActionIcon';
    import ProgressLinearBar from '~/components/Broker/UIComponents/ProgressLinearBar';
    import Scroll from '~/mixins/scrollText';
    import generalHelpers from "../../mixins/generalHelpers";

    export default {
        name: 'BrokerDashboard',
        components: {
            Dashboard,
            StatusChip,
            ActionIcon,
            ProgressLinearBar
        },
        mixins: [Scroll, generalHelpers],
        props: ['info'],
        data() {
            return {
                hasStats: true,
                endpoint: 'broker/risk',
                submissionRoute: 'broker-risk',
                manualSubmissionRoute: 'manual-submission-risk',
                statusFilterOptions: [
                    {
                        value: 'draft',
                        text: 'Draft'
                    },
                    {
                        value: 'in_the_market',
                        text: 'In The Market'
                    },
                    {
                        value: 'ntu',
                        text: 'NTU'
                    },
                    {
                        value: 'declined',
                        text: 'Declined'
                    },
                    {
                        value: 'firm_ordered',
                        text: 'Firm Ordered'
                    },
                    {
                        value: 'bound',
                        text: 'Bound'
                    },
                    {
                        value: 'expired',
                        text: 'Expired'
                    },
                    {
                        value: 'closed',
                        text: 'Closed'
                    },
                    {
                        value: 'off_risk',
                        text: 'Off Risk'
                    },
                    {
                        value: 'not_taken_to_market',
                        text: 'Not taken to market'
                    },
                    {
                        value: 'nbi',
                        text: 'NBI'
                    }
                ],
                tableHeaders: [
                    {
                        text: 'Updated',
                        value: 'updated_at',
                        sortable: true
                    },
                    {
                        text: 'ID',
                        value: 'id',
                        sortable: true
                    },
                    {
                        text: 'Type',
                        value: 'risk_structure',
                        sortable: true
                    },
                    {
                        text: 'Insured',
                        value: 'client_company_name',
                        sortable: true,
                    },
                    {
                        text: 'Obligor/Guarantor',
                        value: 'obligors',
                        sortable: true
                    },
                    {
                        text: 'Country',
                        value: 'country_name',
                        sortable: true
                    },
                    {
                        text: 'Currency',
                        value: 'currency_code',
                        sortable: true
                    },
                    {
                        text: 'Limit of liability',
                        value: 'limit_of_liability',
                        sortable: true,
                        align: 'right',
                    },
                    {
                        text: 'Declaration end',
                        value: 'expiry_date',
                        sortable: true
                    },
                    {
                        text: 'Status',
                        value: 'status',
                        sortable: true,
                        align: 'right'
                    }
                ],
                statFilters: [
                    {
                        key: 'expired-declarations',
                        name: 'Expired declarations',
                        searchKey: 'expired'
                    },
                    {
                        key: 'firm-orders-available',
                        name: 'Firm orders available',
                        searchKey: 'in_the_market',
                    },
                    {
                        key: 'drafts-to-be-submitted',
                        name: 'Drafts to be submitted',
                        searchKey: 'draft'
                    }
                ]
            }
        },
        computed: {
            userIsBrokerAdmin() {
                return this.$root.userIsBrokerAdmin()
            },
            userIsPlacingBroker() {
                return this.$root.user.company && this.$root.user.company.company_type === 'placing_broker';
            },
            riskTypeOptions() {
                let riskTypeOptions = [
                    {
                        value: 'accepted_declaration',
                        text: 'Accepted Declarations'
                    },
                    {
                        value: 'standalone_policy',
                        text: 'Standalone Policies'
                    },
                ];
                if (this.userIsPlacingBroker) {
                    let mst =  {
                        value: 'manual_submission_tracker',
                        text: 'Manual Submissions'
                    };
                    riskTypeOptions.push(mst);
                }
                return riskTypeOptions;
            }

        },
        methods: {
            getLimitOfLiability(contractValue, indemnity) {
                return (indemnity / 100) * contractValue;
            },
            getOfferedPercentage(percentage) {
                percentage = Number(percentage);
                return Math.min(percentage, 100);
            },
            isMultiObligor(risk) {
                return risk.obligors.length > 1 || risk.guarantors.length > 1;
            },
            getUniqueCountryList (obligors) {
                let obligorCountryList = [];
                obligors.forEach(function(obligor) {
                    obligorCountryList.push(obligor.country_name);
                });

                return [...new Set(obligorCountryList)];
            },
            countryName(risk) {
                if (this.isMultiObligor(risk)) {
                    const uniqueObligors = this.getUniqueCountryList(risk.obligors)

                    return uniqueObligors.length > 1 ? 'Multi country' : uniqueObligors.shift();
                }

                return risk.obligors[0]?.country_name;
            }
        }
    }
</script>

<style scoped lang="scss">
 .width-20 {
     width: rem(20px);
 }

 .x-table.theme--dark tbody tr.item-row td span.width-150 {
     width: rem(150px);
 }
</style>
